/**
 * UpgradeModal — Full-screen feature-gate upgrade prompt.
 * Shown within the Subscription Management module when the API returns
 * 402 (payment_required) for a feature the org cannot access on its current plan.
 *
 * The plan that unlocks the feature is rendered from /v1/billing/config, so
 * the name, price, caps and feature list are the same ones the website
 * publishes. Plans with a self-serve checkout open it; the rest go to sales.
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Lock, Check, ArrowUpRight, Loader2, Crown } from "lucide-react";
import { Button } from "../shadcn/button";
import {
  createCheckout,
  getBillingConfig,
  planDisplayName,
  salesMailto,
  formatLimit,
  formatUploadCap,
  type PlanName,
  type PaidPlanName,
} from "../../api/billing";
import { cn } from "../../lib/utils";

export interface UpgradeModalProps {
  /** Feature id from FEATURE_DISPLAY (e.g. "code_health") */
  featureId: string;
  /** Human-readable feature name */
  featureLabel: string;
  /** Which plan first unlocks the feature */
  requiredPlan: PlanName;
  /** Current org plan */
  currentPlan: PlanName;
  onClose: () => void;
}

const FALLBACK_CONTACT = "yash.sancheti@paxleysoftware.com";

export default function UpgradeModal({
  featureLabel,
  requiredPlan,
  currentPlan,
  onClose,
}: UpgradeModalProps) {
  const [loading, setLoading] = React.useState(false);
  const configQuery = useQuery({ queryKey: ["billing", "config"], queryFn: getBillingConfig });
  const config = configQuery.data;
  const contactEmail = config?.contactEmail || FALLBACK_CONTACT;

  const target: PaidPlanName = requiredPlan === "free" ? "starter" : requiredPlan;
  const plan = config?.plans?.[target];
  const requiredName = planDisplayName(target, config);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { url } = await createCheckout(target, "monthly");
      window.location.href = url;
    } catch {
      setLoading(false);
    }
  };

  const highlights = plan
    ? plan.features
        .filter((id) => id !== "sast" && id !== "sca" && id !== "secrets" && id !== "pr_comments")
        .slice(0, 5)
        .map((id) => config?.featureDisplay?.[id] ?? id)
    : [];

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl bg-surface1 border border-border/60 shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label={`Unlock ${featureLabel}`}
      >
        <div className="h-1.5 w-full bg-brand" />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface2/60 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-8">
          <div className="flex flex-col items-center text-center gap-3 mb-7">
            <div className="p-4 rounded-2xl bg-brand/10 border border-brand/20">
              <Lock className="h-8 w-8 text-brand" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Unlock {featureLabel}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Included from the <span className="font-semibold">{requiredName}</span> plan.
                Your organization is on {planDisplayName(currentPlan, config)}.
              </p>
            </div>
          </div>

          <div className={cn("grid gap-4", target !== "enterprise" && "sm:grid-cols-2")}>
            <div className="rounded-xl border border-brand/40 bg-brand/5 p-5 flex flex-col gap-4 ring-2 ring-brand/30">
              <div className="flex items-center justify-between">
                <span className="font-bold text-foreground">{requiredName}</span>
                {currentPlan === target && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400 font-semibold uppercase">
                    Current
                  </span>
                )}
              </div>

              <div>
                {plan ? (
                  <>
                    <span className="text-2xl font-extrabold text-foreground">${Math.round(plan.priceMonthlyCents / 100)}</span>
                    <span className="text-xs text-muted-foreground"> / month</span>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{plan.contract}</p>
                  </>
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>

              {plan && (
                <ul className="space-y-1.5 flex-1 text-xs text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-brand flex-shrink-0" />
                    <span>{formatLimit(plan.maxRepos)} repositories, {formatUploadCap(plan.maxUploadBytes)} per upload</span>
                  </li>
                  {highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-brand flex-shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}

              {plan?.purchasable ? (
                <Button onClick={handleCheckout} disabled={loading} className="w-full gap-2" size="sm">
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
                  Upgrade to {requiredName}
                </Button>
              ) : (
                <Button asChild size="sm" className="w-full gap-2">
                  <a href={salesMailto(target, contactEmail)}>
                    <ArrowUpRight className="h-3.5 w-3.5" /> {target === "enterprise" ? "Contact sales" : `Get ${requiredName}`}
                  </a>
                </Button>
              )}
            </div>

            {target !== "enterprise" && (
              <div className="rounded-xl border border-border/60 bg-surface2/40 p-5 flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-muted-foreground" />
                  <span className="font-bold text-foreground">Enterprise</span>
                </div>
                <div>
                  <span className="text-2xl font-extrabold text-foreground">
                    ${Math.round((config?.plans?.enterprise?.priceMonthlyCents ?? 99900) / 100)}
                  </span>
                  <span className="text-xs text-muted-foreground"> / month</span>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Unlimited repositories, scans and members</p>
                </div>
                <ul className="space-y-1.5 flex-1 text-xs text-muted-foreground">
                  {["Everything in Growth", "SAML SSO", "Dedicated support and SLA", "Audit log export"].map((h) => (
                    <li key={h} className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild variant="outline" size="sm" className="w-full gap-2">
                  <a href={salesMailto("enterprise", contactEmail)}>
                    <ArrowUpRight className="h-3.5 w-3.5" /> Contact sales
                  </a>
                </Button>
              </div>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Priced around the software you protect, never per seat. Full comparison at paxleysoftware.com/pricing
          </p>
        </div>
      </div>
    </div>
  );
}
